/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : updateForm.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { ComponentRegistry } from "./registry";
import { PAGE_SIZES } from "./pageSize";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { Box, Grid, IconButton } from "@mui/material";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import customConsole from "../../../config/customConsole";
import { HandleApiActions } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import { CustomLogsDialog } from "../../../components";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttributeUpdateDialog from "../../labcar/Attributes/AttributeUpdateDialog";

const updatePageElements = (pages, pageIndex, elementId, value) => {
  return pages.map((page, pIdx) => {
    if (pIdx !== pageIndex) return page;
    return {
      ...page,
      elements: page.elements.map((el) =>
        el.id === elementId
          ? { ...el, properties: { ...el.properties, value } }
          : el,
      ),
    };
  });
};

export default function UpdateFormRenderer({
  isOpen,
  onClose,
  data,
  setDynamicFormData,
  setDynamicFormFilteredData,
  selectedComponent,
  componentType,
}) {
  const [template, setTemplate] = useState(data || { fields: [] });
  const pageRefs = useRef([]);
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const [openTemplateChangeLogsDialog, setOpenTemplateChangeLogsDialog] =
    useState(false);
  const [templateChangeLogsList, setTemplateChangeLogsList] = useState([]);
  const [templateLogsDialogData, setTemplateLogsDialogData] = useState({});
  const [isEdited, setIsEdited] = useState(false);
  const [reason, setReason] = useState("");
  const [isPdfMode, setIsPdfMode] = useState(false);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);

  const dispatch = useDispatch();
  const initialValuesRef = useRef(
    Object.fromEntries(
      (data?.fields ?? []).flatMap((page, pageIndex) =>
        (page.elements ?? []).map((el) => [
          `${pageIndex}_${el.id}`,
          el?.properties?.value ?? "",
        ]),
      ),
    ),
  );

  useEffect(() => {
    if (!data) return;

    setTemplate(data);

    initialValuesRef.current = Object.fromEntries(
      (data.fields ?? []).flatMap((page, pageIndex) =>
        (page.elements ?? []).map((el) => [
          `${pageIndex}_${el.id}`,
          el?.properties?.value ?? "",
        ]),
      ),
    );
  }, [data]);

  const getSelectedComponentType = () => {
    console.log("---componentType---", componentType, selectedComponent);
    if (componentType) return String(componentType).toLowerCase();
    if (selectedComponent?.swm_id) return "swm";
    if (selectedComponent?.ee_id) return "ee";
    if (selectedComponent?.vc_id) return "vc";
    if (selectedComponent?.vs_id) return "vs";
    return "vc";
  };


  const updateElementValue = useCallback((pageIndex, elementId, value) => {
    setIsEdited(true);

    setTemplate((prev) => ({
      ...prev,
      fields: updatePageElements(prev.fields, pageIndex, elementId, value),
    }));
  }, []);

  const handleSave = () => {
    updateFieldValues();
  };

  const updateFieldValues = async () => {
    let apiDetails = typeApprovalAPI.update_dynamic_form;
    if (template?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(template?.form_id),
          field_details: template.fields,
        },
        show_toast: true,
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              form_id: Number(template?.form_id),
              field_details: template.fields,
              template_tag: "NEW",
              reason: reason,
            },
            show_toast: true,
          }),
        ),
      );
      if (!resp?.error) {
        initialValuesRef.current = Object.fromEntries(
          (template.fields ?? []).flatMap((page, pageIndex) =>
            (page.elements ?? []).map((el) => [
              `${pageIndex}_${el.id}`,
              el?.properties?.value ?? "",
            ]),
          ),
        );
        forceUpdate();
        setIsEdited(false);

        console.log("Initial values updated after save");
      }
    }
  };

  const handleGetTemplateChangeLogs = async ({ pageNo, pageSize }) => {
    let apiDetails = typeApprovalAPI.get_dynamic_form_logs;

    if (template?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(template?.form_id),
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              form_id: Number(template?.form_id),
              page_number: pageNo,
              page_size: pageSize,
            },
            show_toast: false,
          }),
        ),
      );
      if (resp?.error) {
        setTemplateChangeLogsList([]);
      } else {
        setTemplateChangeLogsList(resp?.data);
      }
    }
  };

  const handleTemplateLogsDialogOpen = (data) => {
    setOpenTemplateChangeLogsDialog(true);

    setTemplateLogsDialogData(data);
  };
  const handleTemplateLogsDialogClose = () => {
    setTemplateLogsDialogData(null);
    setTemplateChangeLogsList([]);

    setOpenTemplateChangeLogsDialog(false);
  };

  const downloadPDF = async () => {
    let pdf = null;

    try {
      setIsPdfMode(true);

      await new Promise((resolve) => setTimeout(resolve, 150));

      for (let i = 0; i < template.fields.length; i++) {
        const page = template.fields[i];
        const ref = pageRefs.current[i];
        if (!ref) continue;

        let pxW = PAGE_SIZES[page.settings.pageSize]?.width || 794;
        let pxH = PAGE_SIZES[page.settings.pageSize]?.height || 1123;

        if (page.settings.orientation === "Landscape") {
          [pxW, pxH] = [pxH, pxW];
        }

        const mmW = (pxW * 25.4) / 96;
        const mmH = (pxH * 25.4) / 96;

        const canvas = await html2canvas(ref, {
          scale: window.devicePixelRatio * 2,
          useCORS: true,
          backgroundColor: page.settings.backgroundColor || "#fff",
        });

        const imgData = canvas.toDataURL("image/png", 1);

        if (pdf) {
          pdf.addPage([mmW, mmH]);
        } else {
          pdf = new jsPDF(
            page.settings.orientation === "Landscape"
              ? "landscape"
              : "portrait",
            "mm",
            [mmW, mmH],
          );
        }

        const imgProps = pdf.getImageProperties(imgData);
        const pdfHeight = (imgProps.height * mmW) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, mmW, pdfHeight, undefined, "FAST");
      }

      pdf?.save(`${template.name || "form"}.pdf`);
    } finally {
      setIsPdfMode(false);
    }
  };

  console.log(reason, "reason,reason");
  return (
    <>
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            gap: 2,
          }}
        >
          <Box>
            {isEdited && (
              <CustomFormTextFieldV2
                label="Enter Reason"
                isMultiline
                placeholder="Enter Reason"
                value={reason}
                txtFldStyle={{ width: 380 }}
                setValue={setReason}
                required={true}
              />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CustomButtonV2
                type="submit"
                size="small"
                btnName="Update"
                disabled={!isEdited || reason.trim() === ""}
                handleOnClick={handleSave}
              />
              <CustomButtonV2
                type="other"
                size="small"
                btnName="Log"
                handleOnClick={() =>
                  handleTemplateLogsDialogOpen({ pageNo: 1, pageSize: 25 })
                }
              />
              <AddButtonV2 icon="download" handleOnClick={downloadPDF} />

              <IconButton
                onClick={() => {
                  console.log("--selectedComponent---", selectedComponent);
                  if (selectedComponent) {
                    setOpenAttributeUpdateDialog(true);
                  }
                }}
                disabled={!selectedComponent}
                aria-label="copy attributes"
              >
                <VisibilityOutlinedIcon />
              </IconButton>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {template.fields?.map((page, pageIndex) => {
            let pxW = PAGE_SIZES[page.settings.pageSize]?.width || 794;
            let pxH = PAGE_SIZES[page.settings.pageSize]?.height || 1123;

            if (page.settings.orientation === "Landscape") {
              [pxW, pxH] = [pxH, pxW];
            }

            const pageKey = page.id || page.pageId || `page-${pageIndex}`;

            return (
              <div
                key={pageKey}
                ref={(el) => (pageRefs.current[pageIndex] = el)}
                style={{
                  position: "relative",
                  width: pxW,
                  height: pxH,
                  margin: "20px auto",
                  background: page.settings.backgroundColor,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                }}
              >
                {page.elements.map((el) => {
                  const reg = ComponentRegistry[el.type];
                  if (!reg?.render) return null;

                  const initialValue =
                    initialValuesRef.current[`${pageIndex}_${el.id}`];

                  return (
                    <div
                      key={el.id}
                      style={{
                        position: "absolute",
                        top: el.y,
                        left: el.x,
                        width: el.width,
                        height: el.height,
                      }}
                    >
                      {reg.render(
                        el,
                        el.properties?.value,
                        (v) => updateElementValue(pageIndex, el.id, v),
                        initialValue,
                        isPdfMode,
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Grid>
      </Grid>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openAttributeUpdateDialog}
        setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        onClickSuccess={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        selectedComponent={selectedComponent}
        componentType={getSelectedComponentType()}
        type="COPY"
        hideDefaultActions
      />

      <CustomLogsDialog
        open={openTemplateChangeLogsDialog}
        handleClose={handleTemplateLogsDialogClose}
        dialogTitle={"Change Logs"}
        dialogContent={templateChangeLogsList}
        data={templateLogsDialogData}
        getLogsData={handleGetTemplateChangeLogs}
      />
    </>
  );
}

UpdateFormRenderer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.shape({
    form_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        settings: PropTypes.shape({
          pageSize: PropTypes.string,
          orientation: PropTypes.string,
          backgroundColor: PropTypes.string,
        }),
        elements: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number,
            width: PropTypes.number,
            height: PropTypes.number,
            properties: PropTypes.object,
          }),
        ),
      }),
    ),
  }),
  setDynamicFormData: PropTypes.func,
  setDynamicFormFilteredData: PropTypes.func,
  selectedComponent: PropTypes.shape({
    swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vc_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vs_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  componentType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};



